import '/assets/styles/main.scss';
import useServerProps from '@wearetla/tla-essentials-tools/hooks/server-props';

// Data
import routes from '/routes';
import layouts from '/views/layouts';
import validations from '/data/validation';

// Layout
import WebLayout from '/views/layouts/_web';
import AppLayout from '/views/layouts/_app';

import { RequestHeadersController } from '/controllers/request-headers';
import { AssetVersionController } from '/controllers/asset-version';
import { GlobalStateController } from '/controllers/global-state';
// import { GtmEventsController } from './controllers/analytics';
import { ModalsProvider, ModalsRenderer } from '@wearetla/tla-essentials-tools/utilities/modals';

// Context & Utils
import {
	NavigatorProvider,
	NavigatorRoutes,
} from '@wearetla/tla-essentials-tools/utilities/navigator';
import { AuthProvider } from '/utilities/auth';
import { ApplicationProvider, useApplication } from '/utilities/application';
import { BasketProvider } from '/controllers/basket';
import { FormsProvider } from '@wearetla/tla-essentials-tools/partials/forms';
import { StorageProvider } from '@wearetla/tla-essentials-tools/utilities/storage';
import { GlobalStateProvider } from '@wearetla/tla-essentials-tools/utilities/global-state';
import { BreakpointsProvider } from '@wearetla/tla-essentials-tools/utilities/breakpoints';
import { Messenger } from '@wearetla/tla-essentials-tools/utilities/messenger';
import { HeadProvider } from '@wearetla/tla-essentials-tools/utilities/head';
import { GlobalEventsProvider } from '@wearetla/tla-essentials-tools/utilities/global-events';
import { LazySentryProvider } from '/utilities/sentry';
import { I18nProvider } from '/utilities/i18n';

// Partials
import Loader from '/views/partials/loader';
import Icon from '@wearetla/tla-essentials-tools/partials/icon';
import customInputTypes from '/views/partials/input-types';

// Modals
import modals from '/views/modals';
import { BranchSelectionProvider } from '/utilities/branch-selection';
import { LotteryProvider } from '/utilities/lottery';
import { MenuProvider } from '/utilities/menu';
import GuestUserController from './controllers/guest-user';
const ModalCloseBtn = (
	<div className="modal-closebtn">
		<button className="closebtn-btn">
			<Icon name="close" />
		</button>
	</div>
);

const App = ({ serverProps: ssrProps }) => {
	const serverProps = useServerProps(ssrProps);

	return (
		<LazySentryProvider>
			<ModalsProvider
				config={{
					layers: 3,
					styles: () => import('/assets/styles/modals/modals.scss'),
					modals: modals,
					closeBtn: ModalCloseBtn,
				}}
			>
				<GlobalStateProvider initialData={serverProps?.sharedData}>
					<GlobalEventsProvider>
						<StorageProvider initialCookies={serverProps?.cookies}>
							<I18nProvider initialLanguage={serverProps?.initialLanguage}>
								<RequestHeadersController />
								<BreakpointsProvider initialState={serverProps?.mediaStatus}>
									<ApplicationProvider
										initialState={serverProps?.isApplication}
										supportedAppFeatures={serverProps?.supportedAppFeatures}
									>
										<BranchSelectionProvider>
											<AuthProvider>
												<LotteryProvider>
													<MenuProvider>
														<FormsProvider validations={validations} inputTypes={customInputTypes}>
															<NavigatorProvider
																serverProps={serverProps}
																pageLoader={<Loader dark />}
																routes={routes}
																layouts={layouts}
															>
																<BasketProvider>
																	<HeadProvider serverProps={serverProps}>
																		<ModalsRenderer />
																		<AssetVersionController />
																		{/* <GtmEventsController /> */}
																		<GlobalStateController />
																		<Messenger />
																		<GuestUserController />
																		<DynamicLayout>
																			<NavigatorRoutes />
																		</DynamicLayout>
																	</HeadProvider>
																</BasketProvider>
															</NavigatorProvider>
														</FormsProvider>
													</MenuProvider>
												</LotteryProvider>
											</AuthProvider>
										</BranchSelectionProvider>
									</ApplicationProvider>
								</BreakpointsProvider>
							</I18nProvider>
						</StorageProvider>
					</GlobalEventsProvider>
				</GlobalStateProvider>
			</ModalsProvider>
		</LazySentryProvider>
	);
};

const DynamicLayout = ({ children }) => {
	const { isApplication } = useApplication();

	const Layout = isApplication ? AppLayout : WebLayout;

	return <Layout>{children}</Layout>;
};

export default App;
